import { StaticDateRangePicker } from "@mui/x-date-pickers-pro/StaticDateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useState, useContext, useEffect } from "react";
import {
  PickersLayoutRoot,
  usePickerLayout,
} from "@mui/x-date-pickers/PickersLayout";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import dayjs from "dayjs";
import "dayjs/locale/ur";
import "dayjs/locale/sd";
import { Box } from "@mui/material";
import { LanguageContext } from "../../../context/LanguageContext";
import { ReactComponent as Arrowicon } from "./../../../assets/icons/blue-arrow.svg";
import { ReactComponent as Calender } from "./../../../assets/icons/calender-grey.svg";
import "./styles.css";
import { UBButton } from "../../../Libraries";
import { getStartAndEndDate } from "../../../utils";
import { sentenceCase } from "../../../constants";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { analytics, triggerLogEvent } from "../../../config/firebase";

const CustomLayout = (props) => {
  const { content, shortcuts } = usePickerLayout(props);
  const {
    singleSelect,
    startDateLabel,
    endDateLabel,
    formattedStartDate,
    formattedEndDate,
    formattedSingleLabel,
  } = props;

  return (
    <PickersLayoutRoot ownerState={props} className="PickersLayoutRoot">
      <div className="shortcuts">{shortcuts}</div>

      <div className={`contentWrapper ${singleSelect ?  "singleSelect" : ""}`}>
        <div className="toolbar customDateRangeToolbar">
          {singleSelect ? (
            <span
              className={`dateRangeDisplay ${formattedSingleLabel ? "hasValue" : ""}`}
            >
              {formattedSingleLabel || startDateLabel}
            </span>
          ) : (
            <>
              <span
                className={`dateRangeDisplay ${formattedStartDate ? "hasValue" : ""}`}
              >
                {formattedStartDate || startDateLabel}
              </span>
              <span className="dateRangeSeparator" aria-hidden="true">
                -
              </span>
              <span
                className={`dateRangeDisplay ${formattedEndDate ? "hasValue" : ""}`}
              >
                {formattedEndDate || endDateLabel}
              </span>
            </>
          )}
        </div>

        <div className="content">{content}</div>
      </div>
    </PickersLayoutRoot>
  );
};

const RangePicker = ({
  onFilterDate,
  className,
  rangeValue = [],
  id = "",
  isOpen,
  showDirectPicker,
  applyBtnText,
  noClearBtn,
  setIsOpen,
  dateFilters,
  singleSelect = false,
  disablePast,
  disableFuture,
  prevSelectedDate,
  minDate,
  maxDate,
  startDateLabel,
  endDateLabel,
}) => {
  const [I18n] = useContext(LanguageContext);
  const DAYJS_LOCALE_MAP = { ur: "ur", snd: "sd" };
  const dayjsLocale = DAYJS_LOCALE_MAP[localStorage.getItem("language")] || "en";

  const [toggle, setToggle] = useState(isOpen ? true : false);

  const [dateRange, setDateRange] = useState({
    range: [null, null],
  });

  const DURATION_LABELS = { 7: "Weekly", 30: "Monthly", 365: "Yearly", 366: "Yearly" };

  const detectMatchingFilter = (range) => {
    if (!range?.[0]) return "";
    const rStart = dayjs(range[0]).startOf("day");
    const rEnd = dayjs(range[1] || range[0]).startOf("day");
    const rangeDays = rEnd.diff(rStart, "day") + 1;
    const todayStart = dayjs().startOf("day");
    const isFullCalendarMonth =
      rStart.date() === 1 && rEnd.isSame(rStart.endOf("month").startOf("day"), "day");

    if (dateFilters?.length) {
      for (const filter of dateFilters) {
        const { startDate, endDate } = getStartAndEndDate(filter);
        if (!startDate || !endDate) continue;
        const fStart = dayjs(startDate.toDate ? startDate.toDate() : startDate).startOf("day");
        const fEnd = dayjs(endDate.toDate ? endDate.toDate() : endDate).startOf("day");
        const filterDays = fEnd.diff(fStart, "day") + 1;
        if (filterDays === rangeDays) {
          if (filterDays === 1 && !rStart.isSame(todayStart, "day")) continue;
          return filter;
        }
        // Full calendar month (28/29/31 days) also matches the monthly filter
        if (filterDays === 30 && isFullCalendarMonth) return filter;
      }
    }

    if (rangeDays === 1) return rStart.isSame(todayStart, "day") ? "Today" : "";
    if (isFullCalendarMonth) return "Monthly";
    return DURATION_LABELS[rangeDays] || "";
  };

  const [selectedDateFilter, setSelectedDateFilter] = useState("");

  useEffect(() => {
    if (!rangeValue.length) {
      setDateRange({ range: [null, null] });
    }
  }, [rangeValue]);

  useEffect(() => {
    setToggle(!!isOpen);
  }, [isOpen]);

  const handleToggle = () => {
    setToggle((prev) => !prev);
    if (setIsOpen) setIsOpen(false);
  };

  // MAIN FIX HERE
  const handleChange = (newValue, ctx) => {
    if (singleSelect) {
      const selected = newValue?.[0] || newValue?.[1];

      if (selected) {
        setDateRange({
          range: [selected, selected], // ✅ force single date
        });
      }
      return;
    }

    // OLD LOGIC (UNCHANGED)
    if (newValue[0] != dateRange.range[0] && !ctx?.shortcut?.label) {
      setDateRange({ ...dateRange, range: [newValue[0], null] });
      setSelectedDateFilter("");
    } else {
      setDateRange({ range: newValue });
      setSelectedDateFilter(detectMatchingFilter(newValue));
    }
  };

  const handleClickAway = () => {
    setToggle(false);
    if (setIsOpen) setIsOpen(false);
  };

  // APPLY FIX
  const onApplyDate = () => {
    const [start, end] = dateRange.range;

    if (singleSelect) {
      if (start) {
        const selectedDate = new Date(new Date(start).setHours(23, 59, 59));
        triggerLogEvent(analytics, "date_range_selected", { mode: "single", date: dayjs(start).format("DD MMM YYYY") });
        onFilterDate(selectedDate); // only one date
      }
      handleToggle();
      return;
    }

    // OLD LOGIC (UNCHANGED)
    if (start && end) {
      const [startDate, endDate] = [
        new Date(new Date(start).setHours(23, 59, 59)),
        new Date(new Date(end).setHours(23, 59, 59)),
      ];
      triggerLogEvent(analytics, "date_range_selected", { mode: "range", start: dayjs(start).format("DD MMM YYYY"), end: dayjs(end).format("DD MMM YYYY") });
      onFilterDate(startDate, endDate, selectedDateFilter);
    } else {
      onFilterDate(null, null, "");
    }
  };

  const onClearBtn = () => {
    setDateRange({
      range: [null, null],
    });
    onFilterDate();
    handleToggle();
  };

  useEffect(() => {
    if (rangeValue.length > 1) {
      const [startDate, endDate] = [
        dayjs(dayjs(rangeValue[0]).format("DD MMM YYYY")),
        dayjs(dayjs(rangeValue[1]).format("DD MMM YYYY")),
      ];
      const range = [startDate, endDate];
      setDateRange({ range });
      setSelectedDateFilter(detectMatchingFilter(range));
    } else if (singleSelect && rangeValue.length === 1) {
      const date = dayjs(dayjs(rangeValue[0]).format("DD MMM YYYY"));
      const range = [date, date];
      setDateRange({ range });
      setSelectedDateFilter(detectMatchingFilter(range));
    } else {
      setSelectedDateFilter("");
    }
  }, [rangeValue, singleSelect]);

  useEffect(() => {
    if (toggle) {
      setTimeout(() => {
        const objDiv = document.getElementById("DateRangePicketBoxId");
        if (objDiv) {
          objDiv.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
        }
      }, 10);
    }
  }, [toggle]);

  const handleSelectDate = (filter) => {
    setSelectedDateFilter(filter);
    
    const { startDate, endDate } = getStartAndEndDate(filter);

    if (!startDate || !endDate) {
      setDateRange({ range: [null, null] });
      return;
    }

    const start = dayjs(startDate.toDate ? startDate.toDate() : startDate);
    const end = dayjs(endDate.toDate ? endDate.toDate() : endDate);

    if (singleSelect) {
      setDateRange({
        range: [start, start], // single mode
      });
    } else {
      setDateRange({
        range: [start, end],
      });
    }
  };

  const formattedStartDate = dateRange.range[0]
    ? dayjs(dateRange.range[0]).format("DD MMM YYYY")
    : "";

  const formattedEndDate = dateRange.range[1]
    ? dayjs(dateRange.range[1]).format("DD MMM YYYY")
    : "";

  const formattedSingleLabel = dateRange.range[0]
    ? dayjs(dateRange.range[0]).format("DD MMM YYYY")
    : "";

  const isDayCountFilterSet = dateFilters?.includes("5 days");

  const getDateFilterLabel = (date) => {
    if (isDayCountFilterSet) {
      const dayCountLabels = {
        "5 days": I18n.five_days || "5 Days",
        "10 days": I18n.ten_days || "10 Days",
        "15 days": I18n.fifteen_days || "15 Days",
        "30 days": I18n.thirty_days || "30 Days",
      };
      return dayCountLabels[date] || date;
    }

    const labels = {
      "Today": I18n.today || "Today",
      "7 days": I18n.weekly || "Weekly",
      "30 days": I18n.monthly || "Monthly",
      "365 days": I18n.yearly || "Yearly",
      "Half yearly": "6 Mon",
      "Yearly": I18n.yearly || "Yearly",
      "This month": I18n.this_month || "This month",
      "Last 3 months": I18n.last_3_months || "Last 3 months",
      "Last 6 months": I18n.last_6_months || "Last 6 months",
      "Last 12 months": I18n.last_12_months || "Last 12 months",
    };
    return labels[date] || date;
  };
  const presetLabel = selectedDateFilter
    ? getDateFilterLabel(selectedDateFilter)
    : null;

  return (
    <>
      <div className="summaryDateBox">
        {!showDirectPicker ? (
          <div onClick={handleToggle} id={id} className="DatePickerContainer">
            <Box className={`date-range-input ${toggle ? "active" : ""}`}>
              {presetLabel ? (
                <Box className="date-text textCapitalizeFirstLetter active">
                  {presetLabel}
                </Box>
              ) : (
                <>
                  <Box
                    className={`date-text textCapitalizeFirstLetter${formattedStartDate ? "active" : ""}`}
                  >
                    {formattedStartDate || I18n.start_date}
                  </Box>

                  {!singleSelect && (
                    <>
                      <Box className="icon-arrow">
                        <Arrowicon />
                      </Box>
                      <Box
                        className={`date-text textCapitalizeFirstLetter${formattedEndDate ? "active" : ""}`}
                      >
                        {formattedEndDate || I18n.end_date}
                      </Box>
                    </>
                  )}
                </>
              )}

              <Box className="calender-icon">
                <Calender />
              </Box>
            </Box>
          </div>
        ) : null}

        {toggle && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <div
              className={`${className} rangePickerWrapper`}
              id="DateRangePicketBoxId"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocale}>
                {singleSelect ? (
                  <StaticDatePicker
                    slots={{
                      layout: (props) => (
                        <CustomLayout
                          {...props}
                          singleSelect={true}
                          toolbarText={formattedSingleLabel} // custom text
                        />
                      ),
                    }}
                    slotProps={{
                      calendarHeader: {
                        views: ["day"], // only day view → removes dropdown
                      },
                    }}
                    value={dateRange.range[0]} // controlled value
                    onChange={(newValue) => {
                      if (newValue) {
                        setDateRange({
                          range: [newValue, newValue],
                        });
                      }
                    }}
                    disablePast={disablePast}
                    disableFuture={disableFuture}
                  />
                ) : (
                  <StaticDateRangePicker
                    slots={{
                      layout: (layoutProps) => (
                        <CustomLayout
                          {...layoutProps}
                          startDateLabel={startDateLabel || I18n.start_date}
                          endDateLabel={endDateLabel || I18n.end_date}
                          formattedStartDate={formattedStartDate}
                          formattedEndDate={formattedEndDate}
                        />
                      ),
                    }}
                    value={dateRange.range}
                    onChange={handleChange}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                )}
              </LocalizationProvider>

              {dateFilters && (
                <Box className="dateFiltersWrapper">
                  {dateFilters.map((date) => (
                    <UBButton
                      key={date}
                      id={`date_range_filter_btn_${date?.toString().toLowerCase().replace(/\s+/g, "_")}`}
                      className={`dateFilterBtn ${selectedDateFilter == date && "selected"}`}
                      title={sentenceCase(getDateFilterLabel(date))}
                      onClick={() => handleSelectDate(date)}
                    />
                  ))}
                </Box>
              )}

              <Box className="dateFilterExportBtn">
                {!noClearBtn && (
                  <UBButton
                    id="date_range_clear_btn"
                    className={"clearDateBtn"}
                    onClick={onClearBtn}
                    title={I18n.clear}
                    disabled={
                      singleSelect
                        ? !prevSelectedDate
                        : !(dateRange.range[0] && dateRange.range[1])
                    }
                  />
                )}

                <UBButton
                  id="date_range_apply_btn"
                  className={"applyDateBtn"}
                  onClick={onApplyDate}
                  title={applyBtnText ? applyBtnText : I18n.apply}
                  disabled={
                    singleSelect
                      ? !dateRange.range[0]
                      : !(dateRange.range[0] && dateRange.range[1]) &&
                        selectedDateFilter?.toLowerCase() != "all"
                  }
                />
              </Box>
            </div>
          </ClickAwayListener>
        )}
      </div>
    </>
  );
};

export default RangePicker;
